import { z } from "../zod.js";
import { NUL_ISSUE, textHasNoNul } from "../common/text.js";
import { Uuid } from "../identity/admin-schemas.js";
import type { SystemRoleCode } from "../permissions.js";
import { WorkOrderStatus } from "../production/schemas.js";
import {
  CustomerStatus,
  CustomerTier,
  CustomersOrder,
  SalesActivitiesOrder,
  SalesActivityDue,
  SalesDateFilter,
} from "../sales/schemas.js";
import {
  OrderStatus,
  OrdersOrder,
  QuoteOrigin,
  QuoteRejectionReason,
  QuoteStatus,
  QuotesOrder,
} from "../sales/quote-schemas.js";
import { OpportunitiesOrder, SalesMonth } from "../sales/opportunity-schemas.js";

// THE SCREENS THAT HAVE ADOPTED SAVED VIEWS (spec 16 §3.12 item 1, D-305
// ruling 2), and the registry that says what each one's view may hold.
//
// A saved view is a snapshot of one list screen's URL state, so its grammar is
// the screen's grammar. The registry names, for every adopting screen, the
// list route the screen reads, the code that route requires (the saved-view
// screen gate, spec 16 §7), each URL filter key with the wire parameter it
// becomes and the values it may hold, and the order grammar with its declared
// default. The config schemas in `./schemas.ts` are built from these entries,
// so the registry is the one place a screen's view grammar is written.
//
// Every query parameter the list route declares is accounted for here. It is
// either a filter's wire parameter, the order parameter, or named in
// `notViewable` with the reason a view does not store it. The contracts test
// reads each route's declared parameters from the registry of routes and
// holds them equal to that set in both directions, so a parameter added to a
// list route fails the test until someone decides whether a view carries it.
//
// The database checks only the key's grammar (`^[a-z]+\.[a-z_]+$`, spec 16
// §2.20), so a later screen's user views need no migration. A later screen
// adopts by adding its key and its entry here, and it must first meet spec 16
// §9.23: none of its filters may touch a field its module withholds.
//
// SIX SCREENS since P3-E2 PR E2-1 (D-314 ruling 6): PR-E4's four, plus
// `sales.opportunities` (`/sales/opportunities`, the deal list) and
// `sales.tasks` (`/sales/tasks`). E2-1 also lets an entry name `readLineages`
// (spec 16 §7): the lineages that read the list where its route narrows by
// lineage, which the saved-view service checks beside the read code. The
// deal list and the customer book name them; the other four do not. New keys
// are APPENDED, never inserted: the e2e debris reporter mirrors this list in
// order (`HARNESS_VIEW_SCREEN_KEYS` in `packages/test-kit/src/harness-codes.ts`,
// held to it by `e2e/unit/views-debris.test.ts`).

export const SAVED_VIEW_SCREEN_KEYS = [
  "production.work_orders",
  "sales.customers",
  "sales.quotes",
  "sales.orders",
  "sales.opportunities",
  "sales.tasks",
] as const;

export type SavedViewScreenKeyName = (typeof SAVED_VIEW_SCREEN_KEYS)[number];

/** The closed enum. An unknown key answers 400 `VALIDATION_FAILED` on every
 * saved-view route, at the query or the body schema. */
export const SavedViewScreenKey = z.enum(SAVED_VIEW_SCREEN_KEYS).openapi("SavedViewScreenKey", {
  description:
    "The list screen a saved view belongs to (spec 16 §3.12 item 1). `production.work_orders` is `/production/work-orders`; `sales.customers`, `sales.quotes` and `sales.orders` are `/sales/customers`, `/sales/quotes` and `/sales/orders`; and since P3-E2 PR E2-1 `sales.opportunities` and `sales.tasks` are `/sales/opportunities` and `/sales/tasks`. The database checks only the grammar `^[a-z]+\\.[a-z_]+$`; this enum is the set of screens that have adopted views.",
});

/** The grammar the database's `screen_key` CHECK holds (spec 16 §2.20). */
export const SAVED_VIEW_SCREEN_KEY_GRAMMAR = /^[a-z]+\.[a-z_]+$/u;

/** One URL filter of a screen: the wire parameter it becomes on the list
 * route and the values a stored view may hold for it. */
export interface SavedViewFilterSpec {
  readonly wire: string;
  readonly schema: z.ZodType;
}

export interface SavedViewScreenSpec {
  readonly key: SavedViewScreenKeyName;
  /** The list route the screen reads. The view's filters and order are
   * parameters of this route, and applying a view runs it as the reader. */
  readonly listRoute: { readonly method: "get"; readonly path: string };
  /** The code the list route requires, and the saved-view screen gate: every
   * saved-view request for this screen is refused 403 `PERMISSION_DENIED`
   * naming it when the caller lacks it (spec 16 §5 and §7). */
  readonly readCode: string;
  /** Present where the list route narrows by LINEAGE, which the read code
   * alone cannot express (spec 16 §7, D-314 ruling 6): the lineages whose
   * holders the list route admits. The saved-view service refuses a caller
   * holding none of them 403 `PERMISSION_DENIED`, as it refuses a caller
   * without the read code, and reads the lineages as the list routes read
   * them, through `roleLineageCodes` (D-224 ruling 7), so a tenant's clone of
   * an admitted role is admitted. Absent means the read code is the whole
   * gate. */
  readonly readLineages?: readonly SystemRoleCode[];
  readonly filters: Readonly<Record<string, SavedViewFilterSpec>>;
  readonly order: {
    /** The URL key and the wire parameter the order rides on. */
    readonly param: "order" | "sort";
    /** The route's order enum. A view's `order` is one of its tokens or null. */
    readonly schema: z.ZodEnum<Readonly<Record<string, string>>>;
    /** The token the route applies when the parameter is absent. A stored
     * `order: null`, an absent order and this token read as equal (spec 16
     * §3.12 items 5 and 6). */
    readonly declaredDefault: string;
  };
  /** Every other wire parameter of the list route, with the reason a view
   * does not store it. */
  readonly notViewable: Readonly<Record<string, string>>;
}

/** A calendar date `YYYY-MM-DD`, the value the work-order list's
 * `due_before` and `due_after` accept at runtime (`DateQueryFilter`). */
const CalendarDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/u)
  .check(z.iso.date())
  .openapi({ format: "date", example: "2026-09-01" });

/**
 * The two free-text fields a saved view carries, its name and a screen's `q`,
 * refuse a NUL character (U+0000) as 400 `VALIDATION_FAILED` with the reason
 * `nul_character`. The rule is the contracts' one rule for free text
 * (`../common/text.ts`), named here as the saved views have always named it.
 */
export const savedViewTextHasNoNul = textHasNoNul;

export const SAVED_VIEW_NUL_ISSUE = NUL_ISSUE;

/** A search box's text. The customer route caps `q` at 200 characters. The
 * work-order route sets no cap, and a work-order view takes the same 200, since
 * no reader types a longer search into the box. A NUL character is refused. */
const SearchText = z
  .string()
  .min(1)
  .max(200)
  .refine(savedViewTextHasNoNul, SAVED_VIEW_NUL_ISSUE)
  .openapi({ description: "1 to 200 characters, with no NUL character (U+0000)." });

const PAGINATION_REASON =
  "Pagination. A view opens its list on the first page, and a cursor names a place in one reading of the list.";

/** The tag filters' REST parameters before their view-grammar entries land
 * (D-323 ruling 6; spec 09 §5's E2-2 rows 20 and 21). The server wave of
 * P3-E2 PR E2-2a1 adds `tag_id` to `GET /v1/customers` and `customer_tag_id`
 * to `GET /v1/quotes` and `GET /v1/orders` as REST parameters. Each joins its
 * book's grammar as a filter in a push of its own (the customer book's in
 * E2-2a1's web wave, then the quote book's, then the order book's), because
 * oasdiff judges every unpushed commit as one diff and cannot pair two changed
 * `oneOf` subschemas of `POST /v1/saved-views`. Until its push the parameter
 * is named here, and the push that adds the filter removes it. The customer
 * book's `tag` has landed (TF1), so only the quote and order books still name
 * their parameter here. */
const TAG_FILTER_PENDING_REASON =
  "A tag filter whose view-grammar entry lands in a push of its own after its REST parameter (D-323 ruling 6). Until then a view does not store it.";

/** The lineages the customer routes, the deal routes and, since D-337, the
 * quote routes admit (spec 09 §7 narrowing 15 for deals and narrowing 23 for
 * quotes; `apps/api/src/modules/sales/customers/customer-door.ts`'s
 * `READ_LINEAGES`): qc and storekeeper hold `sales.read` and are refused all
 * three lists, so a shared view on any of them never reaches them (D-314
 * ruling 6, D-337 ruling 7). */
export const SAVED_VIEW_SALES_BOOK_READ_LINEAGES = [
  "group_admin",
  "facility_admin",
  "sales",
  "finance",
  "planner",
] as const satisfies readonly SystemRoleCode[];

/** `sort` on `GET /v1/work-orders` (spec 04 §5 row 2). The route declares the
 * same three tokens inline, and the contracts test holds the two lists equal. */
export const SAVED_VIEW_WORK_ORDER_SORTS = ["due_date", "priority", "created_at"] as const;
const WorkOrderSort = z.enum(SAVED_VIEW_WORK_ORDER_SORTS);

/**
 * The registry. Keyed by screen key, and `satisfies` keeps each entry's filter
 * keys literal, so the config schemas built from it are typed per screen.
 *
 * Filter values follow the list route's own grammar. Where the route takes a
 * two-value `"true" | "false"` parameter, the view stores a boolean, because
 * a view is URL state and the router reads `?credit_hold=true` as a boolean.
 * `overdue` is the one exception, stored only as `true`, because the
 * work-order route treats `overdue=false` as no filter and a second spelling of
 * "no filter" would let two equal views compare unequal. An empty list or an
 * empty string is not a value either, since the screens write an absent
 * parameter for both.
 */
export const SAVED_VIEW_SCREENS = {
  "production.work_orders": {
    key: "production.work_orders",
    listRoute: { method: "get", path: "/v1/work-orders" },
    readCode: "production.read",
    filters: {
      status: {
        wire: "status",
        schema: z.array(WorkOrderStatus).min(1).max(WorkOrderStatus.options.length),
      },
      facility_id: { wire: "facility_id", schema: Uuid },
      work_center_id: { wire: "work_center_id", schema: Uuid },
      product_id: { wire: "product_id", schema: Uuid },
      due_before: { wire: "due_before", schema: CalendarDate },
      due_after: { wire: "due_after", schema: CalendarDate },
      overdue: { wire: "overdue", schema: z.literal(true) },
      q: { wire: "q", schema: SearchText },
    },
    order: {
      param: "sort",
      schema: WorkOrderSort,
      // Spec 04 §5 row 2: an absent `sort` is `created_at` ascending, the
      // order the server applies (D-305 ruling 17).
      declaredDefault: "created_at",
    },
    notViewable: { cursor: PAGINATION_REASON, limit: PAGINATION_REASON },
  },
  "sales.customers": {
    key: "sales.customers",
    listRoute: { method: "get", path: "/v1/customers" },
    readCode: "sales.read",
    // Since E2-1 (D-314 ruling 6): the book's route already refuses qc and
    // storekeeper by lineage while this gate admitted them on `sales.read`.
    readLineages: SAVED_VIEW_SALES_BOOK_READ_LINEAGES,
    filters: {
      q: { wire: "q", schema: SearchText },
      tier: { wire: "tier", schema: CustomerTier },
      status: { wire: "status", schema: CustomerStatus },
      owner: { wire: "owner_user_id", schema: Uuid },
      credit_hold: { wire: "credit_hold", schema: z.boolean() },
      // The discipline flag (D-314 ruling 9, spec 09 §4.1). The next-step
      // summary is served to every reader of the customer (D-313 ruling 6),
      // so the filter touches nothing §7 withholds (spec 16 §9.23). The route
      // reads `needs_next_step=false` as a 400, so a view stores `true` only.
      // It joined in E2-1's web wave, one change after `sales.opportunities`
      // and `sales.tasks` reached main: adding it in the diff that added those
      // two would have changed one `POST /v1/saved-views` `oneOf` subschema
      // beside two new ones, which oasdiff cannot pair and reports as
      // `request-body-one-of-removed` (ERR). Alone it is one subschema for
      // one, which oasdiff pairs as an additive change.
      needs_next_step: { wire: "needs_next_step", schema: z.literal(true) },
      // The tag filter (D-323 ruling 6, spec 16 §3.12 item 1), the first of
      // the three tag filters, landed alone in E2-2a1's web wave for the
      // reason `needs_next_step` landed alone. A view stores the tag's id and
      // never its name, and an archived tag still filters, so a view naming
      // one keeps answering. Every reader of the customer book is served the
      // customer's tags (spec 09 §7), so the filter touches nothing §7
      // withholds (spec 16 §9.23).
      tag: { wire: "tag_id", schema: Uuid },
    },
    order: { param: "order", schema: CustomersOrder, declaredDefault: "-created_at" },
    notViewable: { cursor: PAGINATION_REASON, limit: PAGINATION_REASON },
  },
  "sales.quotes": {
    key: "sales.quotes",
    listRoute: { method: "get", path: "/v1/quotes" },
    readCode: "sales.read",
    // Since D-337 ruling 7 the quote routes refuse qc and storekeeper by
    // lineage, so this gate does too. Views those two saved before it stay in
    // the table and answer no one, as D-314 accepted for `sales.customers`.
    readLineages: SAVED_VIEW_SALES_BOOK_READ_LINEAGES,
    filters: {
      customer: { wire: "customer_id", schema: Uuid },
      status: { wire: "status", schema: QuoteStatus },
      origin: { wire: "origin", schema: QuoteOrigin },
      owner: { wire: "owner_user_id", schema: Uuid },
      off_list: { wire: "off_list", schema: z.boolean() },
      created_from: { wire: "created_from", schema: SalesDateFilter },
      created_to: { wire: "created_to", schema: SalesDateFilter },
    },
    order: { param: "order", schema: QuotesOrder, declaredDefault: "-created_at" },
    notViewable: {
      customer_tag_id: TAG_FILTER_PENDING_REASON,
      cursor: PAGINATION_REASON,
      limit: PAGINATION_REASON,
    },
  },
  "sales.orders": {
    key: "sales.orders",
    listRoute: { method: "get", path: "/v1/orders" },
    readCode: "sales.read",
    filters: {
      customer: { wire: "customer_id", schema: Uuid },
      status: { wire: "status", schema: OrderStatus },
      facility: { wire: "facility_id", schema: Uuid },
    },
    order: { param: "order", schema: OrdersOrder, declaredDefault: "-confirmed_at" },
    notViewable: {
      shippable:
        "The ship queue's filter (spec 09 §4.8), a different screen. The order book offers no control for it.",
      imported_from_job_id:
        "The import job's link to the orders it loaded (spec 09 §4.19, D-353 ruling 5). The order book offers no control for it.",
      at_risk:
        "Module 03 is absent, so every order reads `at_risk` false and the order book offers no control for it (spec 09 §3.5d).",
      promise_from:
        "Module 03 is absent, so every `promise_date` is NULL and the order book offers no promise-date control (spec 09 §3.5d).",
      promise_to:
        "Module 03 is absent, so every `promise_date` is NULL and the order book offers no promise-date control (spec 09 §3.5d).",
      customer_tag_id: TAG_FILTER_PENDING_REASON,
      cursor: PAGINATION_REASON,
      limit: PAGINATION_REASON,
    },
  },
  // The deal list (spec 09 §4.14, D-314 ruling 6). No filter reads a value
  // (spec 16 §9.23): the value is an ORDER token only, which meets spec 09
  // §7's list-order rule at apply, as `total_minor` does on the quote book.
  "sales.opportunities": {
    key: "sales.opportunities",
    listRoute: { method: "get", path: "/v1/opportunities" },
    readCode: "sales.read",
    readLineages: SAVED_VIEW_SALES_BOOK_READ_LINEAGES,
    filters: {
      // An absent status is `open`, so a view stores `won` or `lost` only and
      // two equal views never compare unequal.
      status: { wire: "status", schema: z.enum(["won", "lost"]) },
      stage: { wire: "stage_id", schema: Uuid },
      owner: { wire: "owner_user_id", schema: Uuid },
      customer: { wire: "customer_id", schema: Uuid },
      close_from: { wire: "close_from", schema: SalesMonth },
      close_to: { wire: "close_to", schema: SalesMonth },
      rotting: { wire: "rotting", schema: z.literal(true) },
      needs_next_step: { wire: "needs_next_step", schema: z.literal(true) },
      lost_reason: { wire: "lost_reason", schema: QuoteRejectionReason },
      q: { wire: "q", schema: SearchText },
    },
    order: {
      param: "order",
      schema: OpportunitiesOrder,
      // Spec 09 §4.14: ties by `created_at` then `id`, the board's order.
      declaredDefault: "expected_close_month",
    },
    notViewable: { cursor: PAGINATION_REASON, limit: PAGINATION_REASON },
  },
  // The task list (spec 09 §4.12, D-314 ruling 6). No `readLineages`:
  // narrowing 10 narrows the list's rows and refuses no lineage. No filter
  // reads a task's words.
  "sales.tasks": {
    key: "sales.tasks",
    listRoute: { method: "get", path: "/v1/sales-activities" },
    readCode: "sales.read",
    filters: {
      // An absent scope is the reader's own tasks, so a view stores `all`
      // only, and a view that does not say `all` reads its reader's own day.
      scope: { wire: "scope", schema: z.literal("all") },
      // An absent status is `open`, so a view stores `done` or `cancelled`.
      status: { wire: "status", schema: z.enum(["done", "cancelled"]) },
      due: { wire: "due", schema: SalesActivityDue },
      customer: { wire: "customer_id", schema: Uuid },
      // Its control is shown to a literal admin only; the route applies it
      // inside narrowing 10, so a stored assignee never widens a reader.
      assignee: { wire: "assignee_user_id", schema: Uuid },
    },
    order: {
      param: "order",
      schema: SalesActivitiesOrder,
      // D-313 ruling 4: PR-E2's status-dependent order, as one token.
      declaredDefault: "worklist",
    },
    notViewable: {
      quote_id:
        "The quote composer reads it for its own open-task line, and the task list offers no control for it (spec 09 §4.12).",
      sales_order_id:
        "The order detail reads it for its own open-task line, and the task list offers no control for it (spec 09 §4.12).",
      opportunity_id:
        "The deal page reads it for its own task card, and the task list offers no control for it (spec 09 §4.12).",
      cursor: PAGINATION_REASON,
      limit: PAGINATION_REASON,
    },
  },
} as const satisfies Record<SavedViewScreenKeyName, SavedViewScreenSpec>;

export type SavedViewScreens = typeof SAVED_VIEW_SCREENS;

/** The registry entry for a key, typed as the general spec. */
export function savedViewScreen(key: SavedViewScreenKeyName): SavedViewScreenSpec {
  return SAVED_VIEW_SCREENS[key];
}
